<script setup lang="ts">
import { computed, type PropType, ref } from 'vue';
import { IconPosition, type CustomIcon } from './interfaces/CustomIcon';
// From its own module, not from `@/components`. This one line was the whole app's entry chunk:
// `CustomButton` is reached from `App.vue` through `errors/` and `common/`, and importing the
// app-wide barrel from here made every component group — surgeon dashboard included, and so
// `DataGraphDialog.vue` and Chart.js — a static dependency of the page that renders the login form.
import ButtonType from './interfaces/ButtonType';
import ProgressSpinner from 'primevue/progressspinner';

const props = defineProps({
  icon: {
    type: Object as PropType<CustomIcon>
  },
  text: {
    type: String
  },
  fontWeight: {
    type: Number
  },
  type: {
    type: String as PropType<ButtonType>
  },
  marginLeft: {
    type: Number,
    default: 0
  },
  disabled: {
    type: Boolean
  },
  loading: {
    type: Boolean
  },
  tooltip: {
    type: String
  }
});

const emit = defineEmits(['onClick']);

const handleClick = (event: MouseEvent) => {
  emit('onClick', event);
};

const marginStart = computed(() => `${props.marginLeft}px`);
const iconMarginRight = computed(() => props.text ? `12px` : `0px`);

const buttonClass = computed(() => `${props.type ? props.type + ' ' : ''}${props.loading ? 'loading ' : ''}`.trim());

const buttonRef = ref(null);

defineExpose({
  buttonRef
});
</script>

<template>
  <button v-ripple
    @click="handleClick"
    ref="buttonRef"
    :disabled="disabled || loading"
    :class="buttonClass"
    :aria-label="text ? undefined : tooltip"
    :style="{
      '--margin-start': marginStart,
      '--icon-margin-right': icon?.position != IconPosition.END ? iconMarginRight : undefined,
      '--icon-margin-left': icon?.position == IconPosition.END ? iconMarginRight : undefined,
      '--font-weight': fontWeight}"
    v-tooltip="tooltip">
      <FontAwesomeIcon :icon="[`${icon.style}`, `${icon.icon}`]"
        class="icon"
        v-if="icon && !loading && icon.position != IconPosition.END"/>
      <ProgressSpinner v-if="loading" strokeWidth="4"/>
      <div class="text" v-if="text">{{ text }}</div>
      <FontAwesomeIcon :icon="[`${icon.style}`, `${icon.icon}`]"
        class="icon"
        v-if="icon && !loading && icon.position == IconPosition.END"/>
  </button>
</template>

<style scoped lang="scss">
@use '../../assets/_custom.button.scss' as *;

// `@use` has to come before any rules, so the shared spinner styles are now
// emitted first. Unset the sides individually instead of using the `margin`
// shorthand so the shared `margin-right` still applies, as it did before.
:deep(.p-progressspinner) {
  margin-top: unset;
  margin-bottom: unset;
  margin-left: unset;
}
</style>
